import React, { useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FileText, Loader2, Mail, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { notificationQueries } from '@/queries/notification/notification.queries';
import { toGMT7 } from '@/services/date';

import { Button } from '../ui/button';
import {
	Popover,
	PopoverClose,
	PopoverContent,
	PopoverTrigger
} from '../ui/popover';

export default function Notification() {
	const { fetchNextPage, data, hasNextPage, isFetchingNextPage } =
		useInfiniteQuery(
			notificationQueries.list({
				pageSize: 7,
				pageNumber: 1
			})
		);

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const scrollPosition = target.scrollHeight - target.scrollTop;

		const threshold = 50;
		const isNearBottom = scrollPosition <= target.clientHeight + threshold;

		if (isNearBottom && hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	};

	const flattenedData = useMemo(() => {
		if (!data) return [];
		return data.pages.flatMap((page) => page.data);
	}, [data]);

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					variant={'outline'}
					size={'icon'}
					className="border-none"
					type="button"
				>
					<Mail color="#000000" absoluteStrokeWidth />
				</Button>
			</PopoverTrigger>
			<PopoverContent className={'min-w-80 p-2'} align="end">
				<div className="flex items-center justify-between border-b px-3 py-2">
					<h3 className="font-semibold text-slate-900">Notifikasi</h3>
					<PopoverClose>
						<Button
							variant="ghost"
							size="icon"
							className="h-8 w-8 text-slate-500 hover:text-slate-900"
						>
							<X className="h-4 w-4" />
						</Button>
					</PopoverClose>
				</div>

				<div
					className="flex max-h-[450px] flex-col overflow-y-auto"
					onScroll={handleScroll}
				>
					{flattenedData.map((notification) => (
						<div
							key={notification.id}
							className={`flex items-start gap-4 border-b bg-white p-2 transition-colors last:border-0 hover:bg-slate-50`}
						>
							<div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-blue-200 bg-blue-50 text-blue-500">
								<FileText className="h-5 w-5" />
							</div>

							<div className="flex flex-col gap-1.5">
								<p className="text-sm leading-none font-semibold text-slate-800">
									{notification.title}
								</p>
								<p className="text-xs leading-snug text-slate-600">
									{notification.description}
								</p>
								<span className="mt-1 text-xs text-slate-500">
									{format(toGMT7(notification.createdDate), "dd MMMM yyyy 'at' HH:mm")}
								</span>
							</div>
						</div>
					))}
					<div
						className={cn(
							'flex h-16 w-full shrink-0 items-center justify-center pt-2 pb-4',
							{
								hidden: !isFetchingNextPage
							}
						)}
					>
						{isFetchingNextPage ? (
							<Loader2 className="h-5 w-5 animate-spin text-slate-400" />
						) : null}
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
}
